import { useEffect, useState } from "react";
import axios from "axios";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { TrendingUp, ChevronDown, Loader2 } from "lucide-react";

import { usePersistedOpen } from "@/lib/usePersistedOpen";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PERIODS = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
];

function fmtDate(d) {
  // "2026-07-19" -> "Jul 19"
  const dt = new Date(d + "T00:00:00Z");
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
}

function StatTile({ label, value, sub }) {
  return (
    <div className="bg-bt-surface-2 border border-bt-border-subtle px-3 py-3">
      <p className="text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary mb-1">{label}</p>
      <p className="text-xl font-mono font-medium text-bt-text-primary">{value}</p>
      {sub && <p className="text-[10px] text-bt-text-tertiary font-body mt-0.5">{sub}</p>}
    </div>
  );
}

function ChartCard({ title, subtitle, children, testid }) {
  return (
    <div className="bg-bt-surface-2 border border-bt-border-subtle p-3 sm:p-4" data-testid={testid}>
      <div className="mb-2">
        <h3 className="font-body text-xs sm:text-sm font-medium text-bt-text-primary">{title}</h3>
        {subtitle && <p className="text-[10px] text-bt-text-tertiary font-body mt-0.5">{subtitle}</p>}
      </div>
      <div className="h-[200px] sm:h-[220px]">
        {children}
      </div>
    </div>
  );
}

export default function AnalyticsDashboard() {
  const [open, setOpen] = usePersistedOpen("analytics", true);
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (d) => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND}/api/admin/analytics/dashboard?days=${d}`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      // silent; keep old data
    }
    setLoading(false);
  };

  useEffect(() => {
    load(days);
  }, [days]);

  const totals = data?.totals || {};

  const pnlBars = (data?.woodrow_pnl || []).map(p => ({
    ...p,
    color: p.pnl >= 0 ? "#22c55e" : "#ef4444",
  }));

  return (
    <div className="bg-bt-surface border border-bt-border-subtle mb-6" data-testid="admin-analytics-dashboard">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-bt-surface-2/40 transition-colors text-left"
        data-testid="toggle-analytics-dashboard"
      >
        <div className="flex items-center gap-3 min-w-0">
          <TrendingUp className="w-5 h-5 shrink-0 text-bt-gold" strokeWidth={1.5} />
          <div className="min-w-0">
            <h2 className="font-heading text-xl font-medium leading-tight text-bt-text-primary">Analytics Dashboard</h2>
            <p className="text-xs text-bt-text-tertiary font-body mt-0.5 truncate">User growth, trade volume, pageviews, and Woodrow P&amp;L distribution</p>
          </div>
        </div>
        <ChevronDown className={`w-5 h-5 text-bt-text-tertiary shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-4 sm:px-6 pb-6">
          {/* Period selector */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex gap-1" data-testid="analytics-period-selector">
              {PERIODS.map(p => (
                <button
                  key={p.days}
                  onClick={() => setDays(p.days)}
                  className={`px-3 py-1.5 text-xs font-mono border transition-colors ${
                    days === p.days
                      ? "bg-bt-gold/20 border-bt-gold text-bt-gold"
                      : "bg-bt-surface-2 border-bt-border-subtle text-bt-text-secondary hover:text-bt-text-primary"
                  }`}
                  data-testid={`analytics-period-${p.label}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {loading && <Loader2 className="w-4 h-4 text-bt-text-tertiary animate-spin" />}
          </div>

          {/* Header tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid="analytics-totals">
            <StatTile label="New Users" value={totals.new_users ?? "—"} sub={`last ${days}d`} />
            <StatTile label="Trades" value={totals.trades ?? "—"} sub={`last ${days}d`} />
            <StatTile label="Pageviews" value={totals.pageviews ?? "—"} sub={`last ${days}d`} />
            <StatTile label="Active Traders" value={totals.active_traders ?? "—"} sub="Woodrow accounts" />
          </div>

          {/* Charts grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* User Growth */}
            <ChartCard
              title="User Growth"
              subtitle="Cumulative users over time"
              testid="chart-user-growth"
            >
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data?.user_growth || []} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--bt-border-subtle))" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={fmtDate} stroke="rgb(var(--bt-text-tertiary))" tick={{ fontSize: 10 }} minTickGap={30} />
                  <YAxis stroke="rgb(var(--bt-text-tertiary))" tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip
                    cursor={{ fill: "rgb(var(--bt-surface-2))" }}
                    contentStyle={{ background: "rgb(var(--bt-surface))", border: "1px solid rgb(var(--bt-border-subtle))", color: "rgb(var(--bt-text-primary))", fontSize: 12 }}
                    labelStyle={{ color: "rgb(var(--bt-text-primary))" }}
                    labelFormatter={fmtDate}
                  />
                  <Line type="monotone" dataKey="total_users" stroke="#eab308" strokeWidth={2} dot={false} name="Total users" />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Trade Volume */}
            <ChartCard
              title="Trade Volume"
              subtitle="Daily Woodrow + user trades"
              testid="chart-trade-volume"
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data?.trade_volume || []} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--bt-border-subtle))" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={fmtDate} stroke="rgb(var(--bt-text-tertiary))" tick={{ fontSize: 10 }} minTickGap={30} />
                  <YAxis stroke="rgb(var(--bt-text-tertiary))" tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip
                    cursor={{ fill: "rgb(var(--bt-surface-2))" }}
                    contentStyle={{ background: "rgb(var(--bt-surface))", border: "1px solid rgb(var(--bt-border-subtle))", color: "rgb(var(--bt-text-primary))", fontSize: 12 }}
                    labelStyle={{ color: "rgb(var(--bt-text-primary))" }}
                    labelFormatter={fmtDate}
                  />
                  <Bar dataKey="woodrow" stackId="a" fill="#eab308" name="Woodrow" />
                  <Bar dataKey="user" stackId="a" fill="#3b82f6" name="User" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Pageviews */}
            <ChartCard
              title="Pageviews"
              subtitle="Daily site pageviews"
              testid="chart-pageviews"
            >
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data?.pageviews || []} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--bt-border-subtle))" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={fmtDate} stroke="rgb(var(--bt-text-tertiary))" tick={{ fontSize: 10 }} minTickGap={30} />
                  <YAxis stroke="rgb(var(--bt-text-tertiary))" tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip
                    cursor={{ fill: "rgb(var(--bt-surface-2))" }}
                    contentStyle={{ background: "rgb(var(--bt-surface))", border: "1px solid rgb(var(--bt-border-subtle))", color: "rgb(var(--bt-text-primary))", fontSize: 12 }}
                    labelStyle={{ color: "rgb(var(--bt-text-primary))" }}
                    labelFormatter={fmtDate}
                  />
                  <Line type="monotone" dataKey="views" stroke="#22c55e" strokeWidth={2} dot={false} name="Views" />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Woodrow P&L Distribution */}
            <ChartCard
              title="Woodrow P&L per User"
              subtitle="Current portfolio value vs $100K baseline"
              testid="chart-woodrow-pnl"
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={pnlBars} margin={{ top: 10, right: 10, left: -10, bottom: 0 }} layout="horizontal">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--bt-border-subtle))" vertical={false} />
                  <XAxis dataKey="user" stroke="rgb(var(--bt-text-tertiary))" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={60} />
                  <YAxis stroke="rgb(var(--bt-text-tertiary))" tick={{ fontSize: 10 }} tickFormatter={(v) => `${v >= 0 ? "+" : ""}${(v / 1000).toFixed(1)}k`} />
                  <Tooltip
                    cursor={{ fill: "rgb(var(--bt-surface-2))" }}
                    contentStyle={{ background: "rgb(var(--bt-surface))", border: "1px solid rgb(var(--bt-border-subtle))", color: "rgb(var(--bt-text-primary))", fontSize: 12 }}
                    labelStyle={{ color: "rgb(var(--bt-text-primary))" }}
                    itemStyle={{ color: "rgb(var(--bt-text-primary))" }}
                    formatter={(value, name, props) => [`$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })} (${props.payload.pnl_pct >= 0 ? "+" : ""}${props.payload.pnl_pct.toFixed(2)}%)`, "P&L"]}
                  />
                  <Bar dataKey="pnl" name="P&L">
                    {pnlBars.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        </div>
      )}
    </div>
  );
}
